import { motion, MotionConfig } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../components/ui/accordion";
import { useInViewOnce } from "../../../hooks/useInViewOnce";

const FAQS = [
  {
    q: "O NeuroMent substitui o sistema de gestão que já uso?",
    a: "Não obrigatoriamente. O NeuroMent pode funcionar como camada pedagógica principal ou ser integrado ao SIS atual (via API, no plano Rede). Para escolas em digitalização, ele funciona de forma independente.",
  },
  {
    q: "A IA toma decisões pedagógicas automaticamente?",
    a: "Não. No núcleo do MVP, decisões de apresentação de conteúdo e ritmo de estudo partem de regras determinísticas configuráveis — não de algoritmos opacos. A IA aparece como assistente de explicação e sugestão, sempre subordinada ao que o professor e a instituição definiram.",
  },
  {
    q: "Como o NeuroMent trata dados de menores de idade e LGPD?",
    a: "Aprendizado e gamificação ficam em camada separada das notas formais. Dados de alunos nunca saem do contexto da instituição contratante. O plano de implantação inclui revisão do contrato de tratamento de dados conforme a LGPD.",
  },
  {
    q: "Em quanto tempo a escola está operacional?",
    a: "Depende do porte e do plano. Campus e Educação têm onboarding guiado com prazo combinado — geralmente de 2 a 6 semanas para configuração inicial. Rede é planejado caso a caso com o gerente de conta.",
  },
  {
    q: "É preciso treinar toda a equipe ao mesmo tempo?",
    a: "Não. O NeuroMent é projetado para adoção incremental. Você pode começar pelo fluxo de professores e notas, e expandir para famílias e coordenação conforme o time vai se ambientando.",
  },
  {
    q: "Existe integração com Google Classroom, Microsoft Teams ou outros LMS?",
    a: "Integrações externas estão no roadmap do plano Rede. Campus e Educação focam no fluxo nativo do NeuroMent. Avaliamos casos específicos durante a proposta comercial.",
  },
  {
    q: "O módulo financeiro já está disponível?",
    a: "O módulo financeiro (matrícula, mensalidades, relatórios) está no roadmap e não faz parte do MVP inicial. Planos já incluem gestão acadêmica, pedagógica e de comunicação com família.",
  },
  {
    q: "Quanto custa? Por que não tem preço na landing?",
    a: "Cada proposta considera porte da escola, número de alunos, módulos e prazo de implantação. Valores tabelados sem contexto seriam enganosos — por isso trabalhamos com consulta e retorno em até 2 dias úteis.",
  },
];

export function FaqSection() {
  const { ref, isVisible } = useInViewOnce();

  return (
    <MotionConfig reducedMotion="user">
      <section
        ref={ref}
        className="border-b border-white/[0.06] py-16 sm:py-20 lg:py-24"
      >
        <div className="container mx-auto max-w-4xl px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: [0, 0, 0.2, 1] }}
            className="mb-12 text-center sm:mb-16"
          >
            <span className="mb-4 inline-block rounded-full border border-slate-500/30 bg-slate-500/10 px-3 py-1 text-xs font-medium text-slate-300 sm:text-sm">
              Perguntas frequentes
            </span>
            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl md:text-4xl">
              Dúvidas antes de{" "}
              <span className="bg-gradient-to-r from-teal-300 to-cyan-300 bg-clip-text text-transparent">
                falar com a gente
              </span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.65, delay: 0.15, ease: [0, 0, 0.2, 1] }}
          >
            <Accordion type="single" collapsible className="w-full space-y-2">
              {FAQS.map((faq, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="rounded-xl border border-white/[0.08] bg-white/[0.03] px-5 backdrop-blur-sm data-[state=open]:border-teal-400/25 data-[state=open]:bg-teal-900/10"
                >
                  <AccordionTrigger className="py-4 text-left text-sm font-medium text-slate-200 hover:text-white hover:no-underline sm:text-base [&[data-state=open]]:text-teal-300">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-4 text-sm leading-relaxed text-slate-400 sm:text-base">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>
    </MotionConfig>
  );
}
